import PropTypes from 'prop-types';
import { useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, styled, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import Iconify from '../../../../components/Iconify';
import conObj from '../../../../utils/connection-assistant';
import useLocales from '../../../../hooks/useLocales';
import { InputContainer, StyledInput } from '../../../../components/GlobalStyles';
import MyDialog from '../../../../components/dialog-component/my-dialog';
// import { closeAuth } from '../../../../redux/slices/authUiSlice';

const snackbarOptionsSuccess = { variant: 'success', autoHideDuration: 5000 };
const snackbarOptionsError = { variant: 'error', autoHideDuration: 5000 };

const FormContainer = styled('form')(() => ({
  marginRight: 'auto',
  marginLeft: 'auto',
  marginTop: '10px',
}));

const ResetPasswordDialog = ({ handleOpen, handleClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { translate } = useLocales();

  const [formData, setFormData] = useState({
    newPassword: {
      value: '',
      show: false,
    },
    confirmPassword: {
      value: '',
      show: false,
    },
    otpCode: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { otpUuid, phoneNumber } = useSelector((state) => state.authUi);
  // useEffect(() => {
  //   if (location?.pathname === '/auth/reset-password' && !location?.state?.otpUuid && !location?.state?.phoneNumber) {
  //     navigate('/');
  //   }
  // }, [location?.pathname, location?.state]);

  const handleFormChange = (e, showName = null) => {
    if (showName) setFormData((prev) => ({ ...prev, [showName]: { show: !prev[showName]?.show } }));
    else
      setFormData((prev) => ({
        ...prev,
        [e.target?.name]: e.target?.name === 'otpCode' ? e.target?.value : { value: e.target?.value },
      }));
  };

  const onSubmit = (e) => {
    e?.preventDefault();

    if (!formData?.otpCode) {
      enqueueSnackbar(translate('x_is_required', { x: translate('verification_code') }), snackbarOptionsError);
      return;
    }

    if (formData?.newPassword?.value.trim().length < 6) {
      enqueueSnackbar(translate('password_atleast_6_characters'), snackbarOptionsError);
      return;
    }

    if (formData?.confirmPassword?.value !== formData?.newPassword?.value) {
      enqueueSnackbar(translate('password_doesnt_match'), snackbarOptionsError);
      return;
    }

    setIsSubmitting(true);

    conObj
      .post(`password_reset_otp.json`, {
        'user-new_password': formData?.newPassword?.value?.trim(),
        'user-no_hp': phoneNumber,
        otp_uuid: otpUuid,
        otp_code: formData?.otpCode,
      })
      .then((res) => {
        if (res.status === 200) {
          const { status } = res.data?.data;
          if (status === 1) {
            enqueueSnackbar(translate('password_reset_success'), snackbarOptionsSuccess);
            // dispatch(closeAuth()); // close modal
            // setOpenLoginSignup({ open: true, isLogin: true });
          }
        }
      })
      .catch((err) => {
        const snackbarOptions = { variant: 'error', autoHideDuration: 5000 };
        enqueueSnackbar(err.message, snackbarOptions);
        console.log(`Server Error: ${err.message}`);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <MyDialog open={handleOpen} close={handleClose}>
      <FormContainer component={'form'} onSubmit={onSubmit}>
        <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '18px', mb: 2 }}>
          {translate('reset_password')}
        </Typography>
        <Stack spacing={2}>
          <Alert severity="success">{translate('otp_sent_enter_code_password')}</Alert>

          <Stack spacing={2}>
            <Box>
              <Box sx={{ mb: 2 }}>
                <InputContainer direction={'row'}>
                  <Iconify icon={'material-symbols:key'} width={24} height={24} sx={{ color: '#55657e' }} />
                  <StyledInput
                    type="number"
                    name="otpCode"
                    value={formData?.otpCode}
                    placeholder={translate('verification_code')}
                    onChange={handleFormChange}
                  />
                </InputContainer>
              </Box>
              <Box sx={{ mb: 2 }}>
                <InputContainer direction={'row'}>
                  <Iconify icon={'ph:lock-light'} width={24} height={24} sx={{ color: '#55657e' }} />
                  <StyledInput
                    type={formData?.newPassword?.show ? 'text' : 'password'}
                    name="newPassword"
                    value={formData?.newPassword?.value}
                    placeholder={translate('new_password')}
                    onChange={handleFormChange}
                  />
                  <IconButton size="small" onClick={(e) => handleFormChange(e, 'newPassword')}>
                    <Iconify
                      icon={formData?.newPassword?.show ? 'ph:eye-light' : 'ph:eye-closed-light'}
                      sx={{ cursor: 'pointer' }}
                    />
                  </IconButton>
                </InputContainer>
              </Box>
              <Box sx={{ mb: 2 }}>
                <InputContainer direction={'row'}>
                  <Iconify icon={'ph:lock-light'} width={24} height={24} sx={{ color: '#55657e' }} />
                  <StyledInput
                    type={formData?.confirmPassword?.show ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData?.confirmPassword?.value}
                    placeholder={translate('confirm_password')}
                    onChange={handleFormChange}
                  />
                  <IconButton size="small" onClick={(e) => handleFormChange(e, 'confirmPassword')}>
                    <Iconify
                      icon={formData?.confirmPassword?.show ? 'ph:eye-light' : 'ph:eye-closed-light'}
                      sx={{ cursor: 'pointer' }}
                    />
                  </IconButton>
                </InputContainer>
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  mb: 2,
                  background: 'linear-gradient(to right, #c34029, #ff7214)',
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  borderRadius: '8px',
                }}
              >
                {translate('confirm')}
              </Button>
            </Box>
          </Stack>
        </Stack>
      </FormContainer>
    </MyDialog>
  );
};

ResetPasswordDialog.propTypes = {
  handleClose: PropTypes.func,
  handleOpen: PropTypes.func,
};

export default ResetPasswordDialog;
